import React, {useCallback, useEffect, useState} from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Switch,
  Typography,
} from '@mui/material'
import {useTranslation} from 'react-i18next'
import {toast} from 'react-toastify'
import paymentMethod from '../../Api/paymentMethod'
import VendorPaymentMethodsApi from '../../Api/vendorPaymentMethods'
import {PAYMENT_COLORS} from './constants'

type LegacyPaymentMethod = {
  id: number
  name?: string | {en?: string; ar?: string} | Record<string, string>
  factory_type?: string
  status?: number | string
  icon?: string
}

function resolveName(
  name: LegacyPaymentMethod['name'],
  lang: string,
  fallback: string
): string {
  if (!name) return fallback
  if (typeof name === 'string') return name
  const record = name as Record<string, string | undefined>
  return record[lang] || record.en || record.ar || Object.values(record).find(Boolean) || fallback
}

function isCashFactory(factory_type: unknown): boolean {
  return String(factory_type || '').toUpperCase() === 'PY_CASH'
}

/** COD / cash collection controls — vendors toggle only; no add gateway. */
export default function CashCollectionTab() {
  const {t, i18n} = useTranslation()
  const [rows, setRows] = useState<LegacyPaymentMethod[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [togglingId, setTogglingId] = useState<number | null>(null)

  const loadMethods = useCallback(() => {
    setLoadError('')
    paymentMethod.get((res: any) => {
      if (res?.ErrorStatus) {
        setLoadError(t('payment.load_error') as string)
        setRows([])
        return
      }
      const methods = Array.isArray(res?.data) ? res.data : []
      setRows(methods.filter((row: LegacyPaymentMethod) => isCashFactory(row.factory_type)))
    }, setIsLoading)
  }, [t])

  useEffect(() => {
    loadMethods()
  }, [loadMethods])

  const isEnabled = (row: LegacyPaymentMethod) => Number(row.status) === 1

  const requestToggle = (item: LegacyPaymentMethod) => {
    const willDisable = isEnabled(item)
    if (!willDisable) {
      doToggle(item)
      return
    }

    // Keep at least one checkout method across legacy + PayConnect gateways.
    setTogglingId(item.id)
    VendorPaymentMethodsApi.list(
      (res: any) => {
        const payConnect = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.data?.data)
            ? res.data.data
            : []
        const payConnectEnabled = payConnect.filter((row: any) => row.is_enabled).length
        setTogglingId(null)

        if (payConnectEnabled < 1) {
          toast.error(t('must_keep_one_payment_method'))
          return
        }
        doToggle(item)
      },
      (loading: boolean) => {
        if (!loading) setTogglingId(null)
      }
    )
  }

  const doToggle = (item: LegacyPaymentMethod) => {
    setTogglingId(item.id)
    paymentMethod.put(
      item.id,
      () => {
        setRows((prev) =>
          prev.map((row) =>
            row.id === item.id
              ? {...row, status: isEnabled(row) ? 0 : 1}
              : row
          )
        )
        setTogglingId(null)
      },
      (loading: boolean) => {
        if (!loading) setTogglingId(null)
      }
    )
  }

  const lang = i18n.language?.startsWith('ar') ? 'ar' : 'en'

  return (
    <Box>
      <Box mb={3}>
        <Typography fontSize={20} fontWeight={600} color={PAYMENT_COLORS.textPrimary}>
          {t('payment.tab_cash') as string}
        </Typography>
        <Typography fontSize={14} color={PAYMENT_COLORS.textSecondary} mt={0.5}>
          {t('payment.cash_subtitle') as string}
        </Typography>
      </Box>

      {loadError ? (
        <Stack gap={2} mb={2}>
          <Alert severity='warning'>{loadError}</Alert>
          <Button variant='outlined' onClick={loadMethods} sx={{alignSelf: 'flex-start'}}>
            {t('payment.retry') as string}
          </Button>
        </Stack>
      ) : null}

      {isLoading ? (
        <Stack alignItems='center' py={8}>
          <CircularProgress />
        </Stack>
      ) : rows.length === 0 ? (
        <Stack
          alignItems='center'
          justifyContent='center'
          py={8}
          gap={1.5}
          sx={{
            border: `1px dashed ${PAYMENT_COLORS.border}`,
            borderRadius: '12px',
            textAlign: 'center',
          }}
        >
          <Typography fontWeight={600} color={PAYMENT_COLORS.textPrimary}>
            {t('payment.cash_empty_title') as string}
          </Typography>
          <Typography fontSize={14} color={PAYMENT_COLORS.textSecondary} maxWidth={400}>
            {t('payment.cash_empty_subtitle') as string}
          </Typography>
        </Stack>
      ) : (
        <Stack gap={2}>
          {rows.map((item) => (
            <Stack
              key={item.id}
              direction={{xs: 'column', sm: 'row'}}
              justifyContent='space-between'
              alignItems={{xs: 'stretch', sm: 'center'}}
              gap={2}
              sx={{
                p: 2,
                backgroundColor: PAYMENT_COLORS.surface,
                border: `1px solid ${PAYMENT_COLORS.border}`,
                borderRadius: '12px',
              }}
            >
              <Stack direction='row' alignItems='center' gap={1.5}>
                {item.icon ? (
                  <Box
                    component='img'
                    src={item.icon}
                    alt=''
                    sx={{width: 40, height: 40, objectFit: 'contain', borderRadius: '8px'}}
                  />
                ) : null}
                <Box>
                  <Typography fontWeight={600} color={PAYMENT_COLORS.textPrimary}>
                    {resolveName(item.name, lang, t('payment.tab_cash') as string)}
                  </Typography>
                  <Typography fontSize={12} color={PAYMENT_COLORS.textSecondary}>
                    PY_CASH
                  </Typography>
                </Box>
              </Stack>

              <Stack direction='row' alignItems='center' gap={1}>
                {togglingId === item.id ? (
                  <CircularProgress size={18} />
                ) : (
                  <Switch
                    checked={isEnabled(item)}
                    onChange={() => requestToggle(item)}
                    size='small'
                    inputProps={{'aria-label': t('payment.tab_cash') as string}}
                  />
                )}
                <Chip
                  size='small'
                  label={
                    isEnabled(item)
                      ? (t('payment.enabled') as string)
                      : (t('payment.disabled') as string)
                  }
                  color={isEnabled(item) ? 'success' : 'default'}
                />
              </Stack>
            </Stack>
          ))}
        </Stack>
      )}
    </Box>
  )
}
